import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Typography,
} from "@suraasa/placebo-ui"
import clsx from "clsx"
import { motion } from "framer-motion"
import { RefObject, useRef } from "react"
import { NavLink } from "react-router-dom"

// import styles from "./Link.module.css"

type LinkProps = {
  icon?: React.ReactNode
  activeIcon?: React.ReactElement
  text: string
  className?: string
  anchorRef?: RefObject<Element | null>
  isOpen?: boolean
  onClose?: () => void
  onToggle?: (val: boolean) => void
} & (
  | {
      href?: never
      isMenu: true
      arrow?: boolean
      menuChildren: React.ReactNode
      isNotification?: boolean
      hasNotification?: boolean
    }
  | {
      href: string
      isMenu?: false
      menuChildren?: never
      isNotification?: false
      hasNotification?: never
    }
)

const Link = ({
  menuChildren,
  icon,
  activeIcon,
  text,
  href,
  className,
  isNotification = false,
  isOpen = false,
  isMenu = false,
  ...props
}: LinkProps) => {
  const linkRef = useRef<HTMLDivElement>(null)

  if (!href && !isMenu) {
    throw new Error("Link supplied without href and not a menu")
  }

  if (isMenu && isNotification) {
    return (
      <DropdownMenu open={isOpen} onOpenChange={(val) => props.onToggle?.(val)}>
        <DropdownMenuTrigger asChild>
          <button
            ref={props.anchorRef as RefObject<HTMLButtonElement>}
            type="button"
            className={clsx(
              "relative !min-w-fit rounded-lg px-1 py-0.75 !text-center text-onSurface-500 transition-colors duration-200 hover:bg-onSurface-100",
              { "!bg-primary-50 text-primary-500": isOpen },
            )}
          >
            {activeIcon && (
              <div
                className={clsx(
                  "absolute transition-all duration-200 ease-linear",
                  {
                    "scale-100 opacity-100": isOpen,
                    "scale-0 opacity-0": !isOpen,
                  },
                )}
              >
                {activeIcon}
              </div>
            )}

            {icon && (
              <div
                className={clsx("transition-all duration-200 ease-linear", {
                  "scale-0 opacity-0": isOpen,
                  "scale-100 opacity-100": !isOpen,
                })}
              >
                {icon}
              </div>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent sideOffset={8}>
          <div className="w-[420px]">{menuChildren}</div>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <NavLink to={href!}>
      {({ isActive }) => (
        <motion.div
          ref={linkRef}
          className={clsx(
            // styles.link,
            "relative flex h-3.5 items-center justify-center rounded-xl text-onSurface-500 transition-colors duration-300 hover:outline hover:outline-primary-100",
            {
              "bg-primary-50 text-primary-600": isActive,
            },
            className,
          )}
          data-active={isActive ? "true" : "false"}
        >
          <Typography
            variant={"button"}
            className={clsx(
              "flex items-center justify-center rounded-xl px-1",
              {
                "gap-0.5": isActive,
              },
            )}
          >
            {(icon || activeIcon) && (
              <motion.span
                initial={{ x: -10, opacity: 0 }}
                animate={{ x: isActive ? 0 : 10, opacity: isActive ? 1 : 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {isActive && (activeIcon || icon)}
              </motion.span>
            )}
            {text}
          </Typography>
          {/* {(icon || activeIcon) && (
            <div className="sm:hidden">{isActive ? activeIcon : icon}</div>
          )} */}
        </motion.div>
      )}
    </NavLink>
  )
}

export default Link
